/**
 * Config Context
 *
 * React context for providing AppConfig to all child components.
 */

'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { AppConfig } from '../types/app-config';

const ConfigContext = createContext<AppConfig | null>(null);

export interface ConfigProviderProps {
  children: ReactNode;
  config: AppConfig;
}

export function ConfigProvider({ children, config }: ConfigProviderProps) {
  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

/**
 * Access the current app configuration.
 * Must be used within a ConfigProvider.
 */
export function useAppConfig(): AppConfig {
  const config = useContext(ConfigContext);
  if (!config) {
    throw new Error('useAppConfig must be used within a ConfigProvider');
  }
  return config;
}

/**
 * Check if a specific feature is enabled in the app config.
 */
export function useFeatureEnabled(feature: AppConfig['features'][number]): boolean {
  const config = useAppConfig();
  return config.features.includes(feature);
}

/**
 * Get a specific entity config by slug.
 */
export function useEntityConfig(slug: string) {
  const config = useAppConfig();
  return config.entities.find(e => e.slug === slug);
}
